import React, { useEffect, useState, useCallback } from "react";
import { Package, ExternalLink } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import APITable from "../../components/Tables/APITable";
import Notification from "../../components/Notification/Notification";
import { TableSkeleton } from "../../components/Skeleton/StatsSkeleton";

/* ===================== SERVICES ===================== */
import { apiGet } from "../../apiService";

/* ===================== STYLES ===================== */
import styles from "./AdminProducts.module.scss";

const AdminProducts = () => {
  /* =========================
     STATE
  ========================== */
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ssoLoadingId, setSsoLoadingId] = useState(null);

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  /* =========================
     HELPERS
  ========================== */
  const showNotification = (msg, type = "success") => {
    setNotification(msg);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  /* =========================
     API CALLS
  ========================== */
  const getAllProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiGet("catalog", "/apimaster/products");
      if (response?.success) {
        setProducts(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      showNotification("Failed to load products", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getAllProducts();
  }, [getAllProducts]);

  /* =========================
     SSO REDIRECT LOGIC
  ========================== */
  const handleSSOClick = async (product) => {
    if (!product?.product_redirect_url) {
      showNotification("Redirect URL is missing for this product.", "error");
      return;
    }

    setSsoLoadingId(product.id);

    try {
      const baseLink = product.product_redirect_url.trim();
      const productId = product.id;
      const base64Id = productId ? btoa(productId) : "";

      const response = await apiGet("user", "/sso/generate");

      if (response?.status === "success" && response?.data?.code) {
        const ssoCode = response.data.code;
        const cleanBaseLink = baseLink.replace(/\/+$/, "");
        const finalUrl = `${cleanBaseLink}?sso_code=${ssoCode}&id=${base64Id}`;

        window.open(finalUrl, "_blank", "noopener,noreferrer");
      } else {
        console.error("Failed to generate SSO code: ", response?.message);
        showNotification(
          response?.message || "Failed to generate SSO token",
          "error",
        );
      }
    } catch (error) {
      console.error("API error during SSO generation:", error);
      showNotification("An error occurred while launching the portal", "error");
    } finally {
      setSsoLoadingId(null);
    }
  };

  /* =========================
     TABLE COLUMNS
  ========================== */
  const columns = [
    { label: "Product Name", key: "name" },
    { label: "Description", key: "description" },
    { label: "Portal Access", key: "link" },
  ];

  /* =========================
     JSX
  ========================== */
  return (
    <div className={styles["admin-products__page"]}>
      {notification && (
        <div className={styles["admin-products__toast-container"]}>
          <Notification message={notification} type={notificationType} />
        </div>
      )}

      {/* HEADER */}
      <div className={styles["admin-products__header"]}>
        <div className={styles["admin-products__icon-wrapper"]}>
          <Package size={24} strokeWidth={2.5} />
        </div>
        <div className={styles["admin-products__title-group"]}>
          <h2 className={styles["admin-products__title"]}>Products</h2>
          <p className={styles["admin-products__subtitle"]}>
            View and securely access all your enrolled products via SSO.
          </p>
        </div>
      </div>

      {/* MAIN CARD */}
      <div className={styles["admin-products__card"]}>
        <div className={styles["admin-products__table-wrapper"]}>
          {loading ? (
            <TableSkeleton columns={3} rows={5} />
          ) : (
            <APITable
              columns={columns}
              data={products}
              renderCell={(row, key) => {
                if (key === "name") {
                  return (
                    <span className={styles["admin-products__name-cell"]}>
                      {row.name}
                    </span>
                  );
                }

                if (key === "description") {
                  return (
                    <div className={styles["admin-products__desc-cell"]}>
                      {row.description || "No description provided."}
                    </div>
                  );
                }

                if (key === "link") {
                  const isGenerating = ssoLoadingId === row.id;
                  return (
                    <button
                      className={styles["admin-products__sso-btn"]}
                      onClick={() => handleSSOClick(row)}
                      disabled={isGenerating || !row.product_redirect_url}
                      title={row.product_redirect_url}
                    >
                      {isGenerating ? "Authorizing..." : "Launch Portal"}
                      {!isGenerating && (
                        <ExternalLink size={16} strokeWidth={2.5} />
                      )}
                    </button>
                  );
                }

                return row[key];
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminProducts;
